import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import {
  getCampaignsPromise,
  deleteCampaignPromise,
  startCampaignPromise,
  pauseCampaignPromise,
  resumeCampaignPromise,
  stopCampaignPromise,
  getLinkedInAccounts,
} from "../services/apiService";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import EmptyState from "../components/shared/EmptyState";
import DropdownMenu from "../components/shared/DropdownMenu";
import LinkedinIcon from "../components/icons/LinkedinIcon";
import { getErrorMessage, notifyError } from "../helpers/apiError";

// --- small presentational icons, scoped to this page ---
const SearchIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M17 17l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KebabIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="4" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="10" cy="16" r="1.6" />
  </svg>
);

const MegaphoneIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8v4h2.5L11 15.5v-11L5.5 8H3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M13.5 7.5a3 3 0 010 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const PersonMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="6.5" r="3.2" />
    <path d="M3.5 17c0-3.3 3-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
  </svg>
);

const EnvelopeMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3 5.5l7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CardMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="4" width="15" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="7" cy="9.3" r="1.4" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 13.5c.4-1.4 1.5-2.1 2.5-2.1s2.1.7 2.5 2.1M12 8.5h4M12 11.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const UsersMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="7" cy="7" r="2.6" />
    <circle cx="14" cy="8" r="2.1" opacity="0.55" />
    <path d="M2 17c0-2.8 2.3-4.6 5-4.6s5 1.8 5 4.6" />
    <path d="M12.2 12.7c2.2.2 3.8 1.8 3.8 4.3h-2.4" opacity="0.55" />
  </svg>
);

const SentMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 3L3 8.8l5 2m9-7.8l-6 14-3.2-6.8m9.2-7.2L8.8 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.5 10.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MinusMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.5 10h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const DashCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.4 2.4" />
  </svg>
);

const CircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-taction" : "bg-gray-200"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-1"
      }`}
    />
  </button>
);

const PerfPill = ({ icon, value }) => (
  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-[12px] font-medium px-2 py-1 rounded-md">
    <span className="text-gray-400">{icon}</span>
    {value}%
  </span>
);

const ProgressPill = ({ icon, value, className }) => (
  <span className={`inline-flex items-center gap-1 text-[12px] font-medium px-2 py-1 rounded-md ${className}`}>
    {icon}
    {value}
  </span>
);

const Campaigns = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const navigate = useNavigate();
  usePageTitle("campaigns");

  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [senderFilter, setSenderFilter] = useState("");
  const [senders, setSenders] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const statusFilterOptions = [
    { value: "DRAFT", label: t("campaign.status.DRAFT") },
    { value: "ACTIVE", label: t("campaign.status.ACTIVE") },
    { value: "PAUSED", label: t("campaign.status.PAUSED") },
    { value: "COMPLETED", label: t("campaign.status.COMPLETED") },
    { value: "CANCELLED", label: t("campaign.status.CANCELLED") },
  ];

  useEffect(() => {
    getLinkedInAccounts({ limit: 100 })
      .then((res) => setSenders(res.data?.data || res.data || []))
      .catch(() => setSenders([]));
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };
      const response = await getCampaignsPromise(params);
      const resData = response.data;

      if (Array.isArray(resData)) {
        setCampaigns(resData);
        setPagination((prev) => ({ ...prev, total: resData.length, totalPages: 1 }));
      } else if (resData?.data) {
        const { data, page, limit, total, totalPages, hasNext, hasPrev } = resData;
        setCampaigns(data || []);
        setPagination({
          page: page || 1,
          limit: limit || 10,
          total: total || 0,
          totalPages: totalPages || 1,
          hasNext: !!hasNext,
          hasPrev: !!hasPrev,
        });
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, t("campaign.toast.error")));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const applyFilters = (overrides = {}) => {
    const nextSearch = overrides.search ?? searchValue;
    const nextStatus = overrides.status ?? statusFilter;
    const nextSender = overrides.linkedinAccountId ?? senderFilter;
    setFilters({
      ...(nextSearch ? { search: nextSearch } : {}),
      ...(nextStatus ? { status: nextStatus } : {}),
      ...(nextSender ? { linkedinAccountId: nextSender } : {}),
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Debounce free-text search so there's no separate "Filter" button
  useEffect(() => {
    const handle = setTimeout(() => {
      applyFilters({ search: searchValue });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const openAddModal = () => {
    navigate(`/${lng}/campaigns/new`);
  };

  const openEditModal = (campaign) => {
    navigate(`/${lng}/campaigns/${campaign.id}/edit`);
  };

  const openView = (campaign) => {
    navigate(`/${lng}/campaigns/${campaign.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("campaign.deleteConfirm", { defaultValue: "Delete this campaign? Its leads will be released back to their lead list." }))) return;
    const original = [...campaigns];
    setCampaigns(campaigns.filter((c) => c.id !== id));
    try {
      await deleteCampaignPromise(id);
      toast.success(t("campaign.toast.deleteSuccess"));
    } catch {
      setCampaigns(original);
      toast.error(t("campaign.toast.error"));
    }
  };

  const handleStart = async (id) => {
    try {
      await startCampaignPromise(id);
      toast.success(t("campaign.toast.startSuccess"));
      fetchCampaigns();
    } catch (err) {
      notifyError(err, t("campaign.toast.error"));
    }
  };

  const handlePause = async (id) => {
    try {
      await pauseCampaignPromise(id);
      toast.success(t("campaign.toast.pauseSuccess"));
      fetchCampaigns();
    } catch (err) {
      notifyError(err, t("campaign.toast.error"));
    }
  };

  const handleResume = async (id) => {
    try {
      await resumeCampaignPromise(id);
      toast.success(t("campaign.toast.resumeSuccess", { defaultValue: t("campaign.toast.startSuccess") }));
      fetchCampaigns();
    } catch (err) {
      notifyError(err, t("campaign.toast.error"));
    }
  };

  const handleStop = async (id) => {
    try {
      await stopCampaignPromise(id);
      toast.success(t("campaign.toast.stopSuccess"));
      fetchCampaigns();
    } catch (err) {
      notifyError(err, t("campaign.toast.error"));
    }
  };

  const renderStatusCell = (campaign) => {
    const status = campaign.status;

    if (status === "DRAFT") {
      return (
        <div className="flex items-center gap-2">
          <DashCircleIcon className="w-4 h-4 text-amber-500" />
          <span className="text-[13px] font-medium text-amber-600">{t("campaign.status.DRAFT")}</span>
        </div>
      );
    }
    if (status === "COMPLETED") {
      return (
        <div className="flex items-center gap-2">
          <CheckMiniIcon className="w-4 h-4 text-green-500" />
          <span className="text-[13px] font-medium text-green-600">{t("campaign.status.COMPLETED")}</span>
        </div>
      );
    }
    if (status === "CANCELLED") {
      return (
        <div className="flex items-center gap-2">
          <MinusMiniIcon className="w-4 h-4 text-red-500" />
          <span className="text-[13px] font-medium text-red-600">{t("campaign.status.CANCELLED")}</span>
        </div>
      );
    }

    // ACTIVE or PAUSED — icon + inline toggle
    const isActive = status === "ACTIVE";
    return (
      <div className="flex items-center gap-2.5">
        <CircleIcon className="w-4 h-4 text-gray-400" />
        <ToggleSwitch
          checked={isActive}
          onChange={() => (isActive ? handlePause(campaign.id) : handleResume(campaign.id))}
        />
      </div>
    );
  };

  const startIndex = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  if (isLoading && campaigns.length === 0) return <LoadingOverlay />;

  return (
    <div className="p-0">
      <h1 className="text-[26px] font-bold text-gray-900 mb-4">{t("campaigns")}</h1>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-bold text-gray-900">{t("campaigns")}</h2>
            <span className="inline-flex items-center justify-center min-w-[28px] h-[24px] px-2 text-[13px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded-md">
              {pagination.total}
            </span>
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold px-4 py-2.5 rounded-lg transition-colors"
          >
            {t("campaign.startNew", { defaultValue: "Start a new campaign" })}
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div className="relative flex-1 min-w-[220px] max-w-[320px]">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters({ search: searchValue })}
              placeholder={t("campaign.searchPlaceholder", { defaultValue: "Search campaigns" })}
              className="w-full pl-10 pr-3 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 transition-colors"
            />
          </div>

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                applyFilters({ status: e.target.value });
              }}
              className="appearance-none pl-9 pr-9 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 min-w-[130px] cursor-pointer"
            >
              <option value="">{t("campaign.allStatuses", { defaultValue: "All" })}</option>
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <MegaphoneIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>

          <div className="relative">
            <select
              value={senderFilter}
              onChange={(e) => {
                setSenderFilter(e.target.value);
                applyFilters({ linkedinAccountId: e.target.value });
              }}
              className="appearance-none pl-9 pr-9 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 min-w-[150px] cursor-pointer"
            >
              <option value="">{t("campaign.allSenders", { defaultValue: "All senders" })}</option>
              {senders.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.accountName || account.email || account.id}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <LinkedinIcon color="currentColor" width="16" height="16" />
            </span>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {error && <div className="px-6 py-3 text-red-500 text-sm">{error}</div>}

        {campaigns.length === 0 ? (
          <div className="p-4">
            <EmptyState message="campaign.noCampaigns" cta="campaign.create" onCtaClick={openAddModal} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[900px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-6 py-3 text-[12px] font-semibold text-gray-500">{t("campaign.status_col")}</th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">{t("campaign.name_col")}</th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">
                      {t("campaign.performance_col", { defaultValue: "Performance" })}
                    </th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">
                      {t("campaign.progress_col", { defaultValue: "Progress" })}
                    </th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">
                      {t("campaign.senders_col", { defaultValue: "Senders" })}
                    </th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">{renderStatusCell(campaign)}</td>

                      <td className="px-4 py-4 min-w-[160px]">
                        <button
                          type="button"
                          onClick={() => openView(campaign)}
                          className="block text-[14px] font-semibold text-gray-900 hover:text-blue-600 text-left truncate max-w-[220px] transition-colors"
                        >
                          {campaign.name || "—"}
                        </button>
                        <span className="block text-[12px] text-gray-400 mt-0.5">
                          {campaign.createdAt
                            ? new Date(campaign.createdAt).toLocaleString([], {
                                month: "numeric",
                                day: "numeric",
                                year: "2-digit",
                                hour: "numeric",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <PerfPill icon={<PersonMiniIcon />} value={campaign.connectionRate ?? 0} />
                          <PerfPill icon={<EnvelopeMiniIcon />} value={campaign.messageRate ?? 0} />
                          <PerfPill icon={<CardMiniIcon />} value={campaign.profileViewRate ?? 0} />
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <ProgressPill
                            icon={<UsersMiniIcon />}
                            value={campaign.totalLeads ?? campaign._count?.leads ?? 0}
                            className="bg-gray-100 text-gray-600"
                          />
                          <ProgressPill
                            icon={<SentMiniIcon />}
                            value={campaign.progress?.sent ?? 0}
                            className="bg-violet-100 text-violet-600"
                          />
                          <ProgressPill
                            icon={<CheckMiniIcon />}
                            value={campaign.progress?.finished ?? 0}
                            className="bg-green-100 text-green-700"
                          />
                          <ProgressPill
                            icon={<MinusMiniIcon />}
                            value={campaign.progress?.failed ?? 0}
                            className="bg-red-100 text-red-600"
                          />
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {campaign.senders?.length ? (
                          <div className="flex -space-x-2">
                            {campaign.senders.slice(0, 3).map((s, i) => (
                              <div
                                key={i}
                                title={s.accountName}
                                className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[11px] font-semibold text-gray-600"
                              >
                                {s.accountName?.[0]?.toUpperCase() || "?"}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-4 py-4 text-right">
                        <CampaignActionsDropdown
                          campaign={campaign}
                          onView={() => openView(campaign)}
                          onEdit={() => openEditModal(campaign)}
                          onDelete={() => handleDelete(campaign.id)}
                          onStart={() => handleStart(campaign.id)}
                          onPause={() => handlePause(campaign.id)}
                          onResume={() => handleResume(campaign.id)}
                          onStop={() => handleStop(campaign.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <div className="text-[13px] text-gray-500">
                {t("campaign.showingPrefix", { defaultValue: "Showing" })}{" "}
                <strong className="text-gray-700 font-semibold">
                  {startIndex}-{endIndex}
                </strong>{" "}
                {t("campaign.showingOf", { defaultValue: "of" })}{" "}
                <strong className="text-gray-700 font-semibold">{pagination.total}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={!pagination.hasPrev}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.previous")}
                </button>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.next")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Custom inline actions dropdown
const CampaignActionsDropdown = ({ campaign, onView, onEdit, onDelete, onStart, onPause, onResume, onStop }) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu trigger={<KebabIcon className="w-4 h-4" />}>
      <button onClick={onView} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
        View
      </button>
      <button onClick={onEdit} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
        {t("campaign.edit")}
      </button>

      {campaign.status === "DRAFT" && (
        <button onClick={onStart} className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-gray-100">
          {t("campaign.start")}
        </button>
      )}

      {campaign.status === "PAUSED" && (
        <button onClick={onResume} className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-gray-100">
          {t("campaign.resume", { defaultValue: "Resume" })}
        </button>
      )}

      {campaign.status === "ACTIVE" && (
        <button onClick={onPause} className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-gray-100">
          {t("campaign.pause")}
        </button>
      )}

      {(campaign.status === "ACTIVE" || campaign.status === "PAUSED") && (
        <button onClick={onStop} className="flex items-center w-full px-4 py-2 text-sm text-orange-700 hover:bg-gray-100">
          {t("campaign.stop")}
        </button>
      )}

      <hr className="my-1" />
      <button onClick={onDelete} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
        {t("campaign.delete")}
      </button>
    </DropdownMenu>
  );
};

export default Campaigns;
